import { cn } from '@poseidon/components/cn';
import { Button, ButtonSegment, Tooltip } from '@poseidon/components/Input2';
import { Development, General } from '@poseidon/components/StrokeIcons';

import type { SequenceStep, SequenceSwitchAttribute, StepIssue } from '~/components/Sequence/sequenceSteps';
import { DEFAULT_SWITCH_ATTRIBUTE, SEQUENCE_SWITCH_ATTRIBUTES, stepIssueRingClass } from '~/components/Sequence/sequenceSteps';
import { SequenceStepIssueTooltip } from '~/components/Sequence/SequenceStepIssueTooltip';

const ATTRIBUTES = Object.keys(SEQUENCE_SWITCH_ATTRIBUTES) as SequenceSwitchAttribute[];

interface Props {
  step: SequenceStep;
  /** A validation problem with this switch (e.g. no branch has a step), or null. */
  invalid: StepIssue | null;
  /** Commit the routing attribute the switch branches on (Age / ~AUM). */
  onChangeAttribute: (attribute: SequenceSwitchAttribute) => void;
  onRemove: () => void;
  /** Locked view (the per-contact contact-detail graph): static attribute label, no remove ✕. */
  readOnly?: boolean;
}

/**
 * The switch node as it sits on the spine — a pill (like the wait node's "N days [days|weeks]") whose
 * inline control is the Age / ~AUM attribute toggle it routes on. The route branches themselves fan out
 * DOWN below this node (see {@link SequenceSwitchBranches}); this pill is only the attribute picker.
 * Dragging the pill background reorders the node; the ✕ removes the whole switch.
 */
export function SequenceSwitchNode({ step, invalid, onChangeAttribute, onRemove, readOnly = false }: Props) {
  const attribute = step.switch?.attribute ?? DEFAULT_SWITCH_ATTRIBUTE;
  return (
    <div className="group/node relative inline-flex items-center">
      <Tooltip
        as="div"
        tooltip={
          invalid != null ? (
            <SequenceStepIssueTooltip issue={invalid} />
          ) : (
            `Switch — routes by ${SEQUENCE_SWITCH_ATTRIBUTES[attribute].label}`
          )
        }
        className={cn(
          'flex h-9 items-center gap-1.5 rounded-full border border-lynch-200 bg-white pl-3 pr-1.5 shadow-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-900',
          !readOnly && 'cursor-grab',
          stepIssueRingClass(invalid),
        )}>
        <Development.Dataflow03 className="size-4 shrink-0 text-lynch-500 dark:text-lynch-400" />
        {readOnly && (
          <span className="whitespace-nowrap px-2 text-xs font-medium text-lynch-600 dark:text-lynch-300">
            {SEQUENCE_SWITCH_ATTRIBUTES[attribute].label}
          </span>
        )}
        {!readOnly && (
          <ButtonSegment size="xs" selectedId={attribute} onChange={onChangeAttribute}>
            {ATTRIBUTES.map((a) => (
              <Button key={a} id={a}>
                {SEQUENCE_SWITCH_ATTRIBUTES[a].label}
              </Button>
            ))}
          </ButtonSegment>
        )}
      </Tooltip>

      {/* Corner ✕ (matches the step node), confirm-first so a stray click can't drop the whole switch.
          Hidden in the locked (per-contact) view — structure can't change there. */}
      {!readOnly && (
        <Button
          size="2xs"
          color="red"
          tone="soft"
          aria-label="Remove step"
          tooltip="Remove switch"
          icon={<General.XMark />}
          onClick={onRemove}
          confirm={{ title: 'Remove this switch?', confirmLabel: 'Remove', position: 'top span-left' }}
          className="pointer-events-none absolute -right-1.5 -top-1.5 opacity-0 transition-opacity group-hover/node:pointer-events-auto group-hover/node:opacity-100"
        />
      )}
    </div>
  );
}
